import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { cn } from '@/lib/utils';
import { PanelTopHeader } from '@/components/panels/PanelTopHeader/PanelTopHeader';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ExitSetupConfirmDialog } from '../ExitSetupConfirmDialog/ExitSetupConfirmDialog';
import { SourceSelectionStep } from '../SourceSelectionStep/SourceSelectionStep';
import { DestinationStep } from '../DestinationStep/DestinationStep';
import { FieldMappingStep } from '../FieldMappingStep/FieldMappingStep';
import { NameStep } from '../NameStep/NameStep';
import {
  getWorkflowSources,
  getWorkflow,
  createWorkflow,
  updateWorkflow,
} from '@/services/workflowApi';
import type { WorkflowSourceItem, DestinationActionSettings } from '@/types/workflow';
import { useAppSelector } from '@/store/hooks';
import { selectCurrentPage } from '@/store/pages/selectors';
import { useMixpanel } from '@/hooks/useMixpanel';
import {
  VIEWED_AUTOMATION_STEP,
  ACTIVATED_AN_AUTOMATION,
  AUTOMATION_ACTIVATION_FAILED,
  EXITED_AUTOMATION_SETUP,
} from '@/analytics/mixpanel/constants/events';
import { STEP, INTEGRATION_NAME, ERROR_TYPE } from '@/analytics/mixpanel/constants/properties';

export type AutomationSetupStep = 'source' | 'destination' | 'fieldMapping' | 'name';

// WHY: When the integration doesn't support field mapping the step is skipped entirely,
// so the indicator shows 3 dots. Otherwise all 4 steps are shown.
function totalSteps(hasFieldMapping: boolean): number {
  return hasFieldMapping ? 4 : 3;
}

function stepToIndex(step: AutomationSetupStep, hasFieldMapping: boolean): number {
  switch (step) {
    case 'source':
      return 0;
    case 'destination':
      return 1;
    case 'fieldMapping':
      return 2;
    case 'name':
      // When field mapping is skipped, name is the 3rd step (index 2).
      return hasFieldMapping ? 3 : 2;
    default:
      return 0;
  }
}

/** Strip "custom." prefix and capitalize for display (e.g. "custom.brevo" → "Brevo"). */
function formatProviderName(provider: string): string {
  const raw = provider.startsWith('custom.') ? provider.slice('custom.'.length) : provider;
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

const CheckIcon: React.FC = () => (
  <svg
    width="9"
    height="9"
    viewBox="0 0 10 10"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M1.5 5l2.5 2.5 4.5-4.5" />
  </svg>
);

const StepIndicator: React.FC<{ currentStep: number; steps: number }> = ({
  currentStep,
  steps,
}) => (
  <div className="flex items-center" data-testid="step-indicator">
    {Array.from({ length: steps }, (_, i) => (
      <React.Fragment key={i}>
        <div
          className={cn(
            'flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full text-[11px] font-semibold',
            i < currentStep
              ? 'bg-green-100 text-green-600'
              : i === currentStep
                ? 'bg-violet-600 text-white'
                : 'border border-neutral-300 bg-white text-neutral-400',
          )}
          data-testid={`step-dot-${i + 1}`}
        >
          {i < currentStep ? <CheckIcon /> : i + 1}
        </div>
        {i < steps - 1 && (
          <div className={cn('h-px flex-1', i < currentStep ? 'bg-green-200' : 'bg-neutral-200')} />
        )}
      </React.Fragment>
    ))}
  </div>
);

export interface AutomationSetupProps {
  currentPageId: string;
  /** Provide when editing an existing automation. Omit for new automations. */
  workflowId?: string;
  initialStep?: AutomationSetupStep;
  /** Called when the user confirms they want to leave setup (returns to list). */
  onExitSetup: () => void;
  /** Called when the user clicks the × close button to close the entire panel. */
  onClosePanel: () => void;
}

/**
 * Ensure currentPageId is always in the source list.
 * For new automations this is the only source; for edits we prepend if missing.
 */
function buildInitialSourceIds(currentPageId: string, existingSourceIds?: string[]): string[] {
  if (!existingSourceIds || existingSourceIds.length === 0) return [currentPageId];
  if (existingSourceIds.includes(currentPageId)) return existingSourceIds;
  return [currentPageId, ...existingSourceIds];
}

export const AutomationSetup: React.FC<AutomationSetupProps> = ({
  currentPageId,
  workflowId,
  initialStep = 'source',
  onExitSetup,
  onClosePanel,
}) => {
  const isEditing = !!workflowId;
  const currentPageData = useAppSelector(selectCurrentPage);
  const track = useMixpanel();

  const [step, setStep] = useState<AutomationSetupStep>(initialStep);
  const [sourceIds, setSourceIds] = useState<string[]>([currentPageId]);
  const [destinationSettings, setDestinationSettings] = useState<DestinationActionSettings | null>(
    null,
  );
  const [availableSources, setAvailableSources] = useState<WorkflowSourceItem[]>([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  // Prevents advancing to destination before loadWorkflowForEdit completes.
  // If the user clicks Next while destinationSettings is still null, DestinationStep
  // mounts with initialSettings=undefined (ref never set) and field mapping is lost.
  const [workflowLoading, setWorkflowLoading] = useState(isEditing);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  // Tracks in-progress field mapping so navigating back without clicking
  // Save & Continue still preserves whatever the user has mapped so far.
  const pendingFieldMappingRef = useRef<Record<string, string>>({});
  const [supportsFieldMapping, setSupportsFieldMapping] = useState(true);

  const [workflowName, setWorkflowName] = useState('');
  const [loadedIsActive, setLoadedIsActive] = useState(true);
  const [loadedWorkflowMeta, setLoadedWorkflowMeta] = useState<{
    id: string;
    created: string;
    updated: string;
  } | null>(null);
  const [activating, setActivating] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);
  const [activateSuccess, setActivateSuccess] = useState(false);

  /**
   * WHY: Ensure the current page always appears in the sources list immediately,
   * using the Redux page name so the chip renders before the API response arrives.
   */
  const effectiveSources = useMemo((): WorkflowSourceItem[] => {
    const currentInSources = availableSources.some((s) => s._meta.id === currentPageId);
    if (currentInSources) return availableSources;
    const currentPageFallback: WorkflowSourceItem = {
      _meta: { id: currentPageId, created: '', updated: '' },
      name: currentPageData?.name ?? 'Untitled Page',
      type: 'Builder4Page',
    };
    return [currentPageFallback, ...availableSources];
  }, [availableSources, currentPageId, currentPageData]);

  const loadAvailableSources = useCallback(async () => {
    setSourcesLoading(true);
    try {
      const response = await getWorkflowSources();
      setAvailableSources(response._items ?? []);
    } catch {
      setAvailableSources([]);
    } finally {
      setSourcesLoading(false);
    }
  }, []);

  const loadWorkflowForEdit = useCallback(async () => {
    if (!workflowId) return;
    try {
      const workflow = await getWorkflow(workflowId);
      setSourceIds(buildInitialSourceIds(currentPageId, workflow.sourceIds));
      setWorkflowName(workflow.name ?? '');
      setLoadedIsActive(workflow.isActive ?? true);
      if (workflow._meta) {
        setLoadedWorkflowMeta(workflow._meta);
      }
      if (workflow._status.code >= 400) throw new Error(`${workflow._status.code}`);
      const firstDestination = workflow.destinationActionSettings?.[0];
      if (firstDestination) {
        setDestinationSettings({
          ...firstDestination,
          fieldMapping: firstDestination.fieldMapping ?? {},
        });
      }
    } catch {
      setSourceIds([currentPageId]);
    } finally {
      setWorkflowLoading(false);
    }
  }, [workflowId, currentPageId]);

  useEffect(() => {
    void loadAvailableSources();
    if (isEditing) {
      void loadWorkflowForEdit();
    }
  }, [loadAvailableSources, isEditing, loadWorkflowForEdit]);

  useEffect(() => {
    if (activateSuccess) return;
    track(VIEWED_AUTOMATION_STEP, { [STEP]: step });
  }, [step, activateSuccess, track]);

  const handleExitClick = () => {
    if (isEditing) {
      // WHY: editing automations may have unsaved source changes — confirm before discarding.
      setShowExitConfirm(true);
    } else {
      track(EXITED_AUTOMATION_SETUP, { [STEP]: step });
      onExitSetup();
    }
  };

  const handleDestinationComplete = (
    settings: DestinationActionSettings,
    integrationSupportsFieldMapping: boolean,
  ) => {
    const destinationChanged =
      !destinationSettings ||
      destinationSettings.credentialId !== settings.credentialId ||
      destinationSettings.actionId !== settings.actionId;
    setDestinationSettings({
      ...settings,
      fieldMapping: destinationChanged ? {} : (destinationSettings?.fieldMapping ?? {}),
    });
    setSupportsFieldMapping(integrationSupportsFieldMapping);
    if (integrationSupportsFieldMapping) {
      if (destinationChanged && !isEditing) {
        // Clear the auto-generated name so it regenerates from the new integration.
        // When editing, the user's saved name is preserved regardless of destination change.
        setWorkflowName('');
      }
      setStep('fieldMapping');
    } else {
      // WHY: skip field mapping step for integrations that don't support it (e.g. email
      // notifications). The step indicator shrinks to 3 dots so the user sees no gap.
      // Generate the default name here since handleFieldMappingComplete won't run.
      if (!workflowName || (destinationChanged && !isEditing)) {
        const pageName = currentPageData?.name ?? 'My Page';
        setWorkflowName(
          `${pageName} → ${settings.providerName ?? formatProviderName(settings.provider)}`,
        );
      }
      setActivateError(null);
      setStep('name');
    }
  };

  const handleFieldMappingComplete = (fieldMapping: Record<string, string>) => {
    if (!destinationSettings) return;
    setDestinationSettings({ ...destinationSettings, fieldMapping });
    // Set default name on first visit; preserve any name the user has already typed.
    if (!workflowName) {
      const pageName = currentPageData?.name ?? 'My Page';
      const integrationName =
        destinationSettings.providerName ?? formatProviderName(destinationSettings.provider);
      setWorkflowName(`${pageName} → ${integrationName}`);
    }
    setActivateError(null);
    setStep('name');
  };

  const handleActivate = async () => {
    if (!destinationSettings) return;
    const trimmedName = workflowName.trim();
    if (!trimmedName) return;

    setActivating(true);
    setActivateError(null);
    const integrationName =
      destinationSettings.providerName ?? formatProviderName(destinationSettings.provider);
    try {
      // WHY: Editing an existing automation exits immediately — the user is already in
      // the list view context and will see the updated automation there.
      // New automations show the success screen so the user gets explicit confirmation
      // before leaving the flow (they can also create another from that screen).
      if (isEditing && workflowId) {
        await updateWorkflow({
          _meta: loadedWorkflowMeta ?? { id: workflowId, created: '', updated: '' },
          name: trimmedName,
          sourceIds,
          destinationActionSettings: [destinationSettings],
          isActive: loadedIsActive,
        });
        track(ACTIVATED_AN_AUTOMATION, { [INTEGRATION_NAME]: integrationName });
        onExitSetup();
      } else {
        await createWorkflow({
          name: trimmedName,
          sourceIds,
          destinationActionSettings: [destinationSettings],
          isActive: true,
        });
        track(ACTIVATED_AN_AUTOMATION, { [INTEGRATION_NAME]: integrationName });
        setActivateSuccess(true);
      }
    } catch (e) {
      track(AUTOMATION_ACTIVATION_FAILED, {
        [INTEGRATION_NAME]: integrationName,
        [ERROR_TYPE]: e instanceof Error ? e.name : 'UnknownError',
      });
      setActivateError('Something went wrong. Check your field mapping and try again.');
    } finally {
      setActivating(false);
    }
  };

  const handleExitConfirmed = () => {
    setShowExitConfirm(false);
    track(EXITED_AUTOMATION_SETUP, { [STEP]: step });
    onExitSetup();
  };

  const handleNextStep = () => {
    if (step === 'source') {
      setStep('destination');
    }
  };

  const handleBackStep = () => {
    if (step === 'destination') {
      setStep('source');
    } else if (step === 'fieldMapping') {
      if (destinationSettings && pendingFieldMappingRef.current) {
        setDestinationSettings({
          ...destinationSettings,
          fieldMapping: pendingFieldMappingRef.current,
        });
      }
      setStep('destination');
    } else if (step === 'name') {
      setActivateError(null);
      // Go back to field mapping if the integration supports it, otherwise back to destination.
      setStep(supportsFieldMapping ? 'fieldMapping' : 'destination');
    }
  };

  const handleCreateAnother = () => {
    setStep('source');
    setSourceIds([currentPageId]);
    setDestinationSettings(null);
    setWorkflowName('');
    setActivateSuccess(false);
    setActivateError(null);
    setSupportsFieldMapping(true);
    pendingFieldMappingRef.current = {};
  };

  const handleAddSource = (sourceId: string) => {
    setSourceIds((prev) => [...prev, sourceId]);
  };

  const handleRemoveSource = (sourceId: string) => {
    setSourceIds((prev) => prev.filter((id) => id !== sourceId));
  };

  return (
    <div className="flex h-full max-w-[280px] flex-col">
      <PanelTopHeader
        text="Integrations"
        backButton={false}
        onClickClose={onClosePanel}
        className=""
        saveBtnShow={false}
      />

      <ScrollArea className="flex w-full flex-1 overflow-hidden px-4 py-4 [&_[data-slot=scroll-area-viewport]>div]:!block">
        {/* Top navigation: hidden in success state; Automations link on source; Back on other steps */}
        {!activateSuccess && step === 'source' ? (
          <button
            onClick={handleExitClick}
            className="mb-4 flex items-center gap-1 text-[12px] text-neutral-500 transition-colors hover:text-neutral-800"
            data-testid="back-to-automations"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.1"
              aria-hidden="true"
            >
              <path d="M8 2L4 6l4 4" />
            </svg>
            Automations
          </button>
        ) : !activateSuccess ? (
          <button
            onClick={handleBackStep}
            className="mb-4 flex items-center gap-1 text-[12px] text-neutral-500 transition-colors hover:text-neutral-800"
            data-testid="back-step-button"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.1"
              aria-hidden="true"
            >
              <path d="M8 2L4 6l4 4" />
            </svg>
            Back
          </button>
        ) : null}

        {/* Step progress indicators — hidden after successful activation */}
        {!activateSuccess && (
          <StepIndicator
            currentStep={stepToIndex(step, supportsFieldMapping)}
            steps={totalSteps(supportsFieldMapping)}
          />
        )}

        {/* Step content */}
        <div className="mt-5">
          {step === 'source' && (
            <SourceSelectionStep
              selectedSourceIds={sourceIds}
              currentPageId={currentPageId}
              availableSources={effectiveSources}
              sourcesLoading={sourcesLoading}
              onAddSource={handleAddSource}
              onRemoveSource={handleRemoveSource}
            />
          )}

          {step === 'destination' && (
            <DestinationStep
              initialSettings={destinationSettings ?? undefined}
              onComplete={handleDestinationComplete}
              onSupportsFieldMappingChange={setSupportsFieldMapping}
            />
          )}

          {step === 'fieldMapping' && destinationSettings && (
            <FieldMappingStep
              destinationSettings={destinationSettings}
              sourceIds={sourceIds}
              initialFieldMapping={destinationSettings.fieldMapping}
              onComplete={handleFieldMappingComplete}
              onMappingChange={(mapping) => {
                pendingFieldMappingRef.current = mapping;
              }}
            />
          )}

          {step === 'name' && destinationSettings && (
            <NameStep
              name={workflowName}
              onChange={(value) => {
                setWorkflowName(value);
                setActivateError(null);
              }}
              onActivate={() => {
                void handleActivate();
              }}
              activating={activating}
              activateError={activateError}
              activateSuccess={activateSuccess}
              integrationName={
                destinationSettings.providerName ?? formatProviderName(destinationSettings.provider)
              }
              onDone={onExitSetup}
              onCreateAnother={handleCreateAnother}
              isEditing={isEditing}
              reviewSummary={{
                source:
                  sourceIds.length === 1
                    ? (effectiveSources.find((s) => s._meta.id === sourceIds[0])?.name ??
                      'Unknown page')
                    : `${sourceIds.length} pages`,
                destination:
                  destinationSettings.providerName ??
                  formatProviderName(destinationSettings.provider),
                ...(supportsFieldMapping && {
                  fieldsMapped: Object.keys(destinationSettings.fieldMapping).length,
                }),
              }}
            />
          )}
        </div>
      </ScrollArea>

      {/* Bottom navigation — Cancel/Next only on source step */}
      {step === 'source' && (
        <div className="flex gap-2 border-t border-neutral-100 px-4 py-3">
          <button
            onClick={handleExitClick}
            className="flex-1 rounded-[8px] border border-neutral-200 px-4 py-[9px] text-[13px] font-medium text-neutral-600 transition-colors hover:bg-neutral-50"
          >
            Cancel
          </button>
          <button
            onClick={handleNextStep}
            disabled={workflowLoading}
            className="flex-1 rounded-[8px] bg-violet-600 px-4 py-[9px] text-[13px] font-medium text-white transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="source-next-button"
          >
            {workflowLoading ? 'Loading…' : 'Next →'}
          </button>
        </div>
      )}

      <ExitSetupConfirmDialog
        isOpen={showExitConfirm}
        onStay={() => setShowExitConfirm(false)}
        onLeave={handleExitConfirmed}
      />
    </div>
  );
};
